import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BlockStack,
  Button,
  Card,
  Collapsible,
  InlineStack,
  Layout,
  Select,
  Text,
} from "@shopify/polaris";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { endOfYear, format, startOfYear } from "date-fns";
import DateRangePicker from "features/datepicker/date-range-picker";
import PaginationControls from "components/pagination/PaginationControls";
import Loading from "components/loading/Loading";
import { btnPrimaryCls } from "components/tw/classes";
import { fetchCabinetSummaryThunk } from "features/cabinet-tasks/cabinet-summary.slice";
import LeaveRequestSheet from "features/leave-request-sheet/leave-request-sheet";
import useIsPhone from "features/files/use-is-phone";
import { useAppDispatch } from "hooks";
import {
  fetchEmployeeLeaveRequests,
  type SerializedLeaveRequest,
} from "services/leave-requests.service";
import {
  FIXED_LEAVE_TYPE_CODES,
  leaveCalendarLeaveTypeI18nKey,
} from "features/leave-calendar/leaveCalendar.constants";
import { UserProfileLeavesCalendar } from "./user-profile-leaves-calendar";
import LeaveRow from "./leave-row";
import LeaveUsageLine from "./leave-usage-line";

const LEAVE_STATUSES = [
  "draft",
  "pending_approval",
  "approved",
  "rejected",
  "cancelled",
] as const;

interface UserProfileLeavesProps {
  /** Defaults to the route's :companyId (the internal profile). */
  companyId?: string;
  /** Defaults to the route's :userId (the internal profile). */
  userId?: string;
  /**
   * View only — the employee card shows someone else's requests: no signing
   * from here (the calendar link and document preview stay).
   */
  readOnly?: boolean;
  /**
   * Personnel card only: show the 12-month wall calendar above the list, and
   * collapse the list itself. The internal profile page (/profile/:userId/leaves)
   * keeps its current list-only view.
   */
  showWallCalendar?: boolean;
  /** With the wall calendar: start with the request list open (the cabinet) instead of collapsed (the personnel card). */
  listInitiallyOpen?: boolean;
  /** Used to label the leave's owner if the no-document view modal is opened from the calendar. */
  employeeName?: string;
  employeeEmail?: string;
  /**
   * The person's own cabinet (/profile/:me/leaves) only. Adds the days used this year, the
   * button and sheet for a new request (also opened by ?new=1 in the address), the comment
   * of each request and "Atšaukti prašymą" on a pending one. The personnel card and the
   * HR views leave it off and show none of this.
   */
  isOwnCabinet?: boolean;
  /** Who signs a new request after the person (cabinet only); none when the person has no manager. */
  managerName?: string | null;
}

function UserProfileLeaves({
  companyId: companyIdProp,
  userId: userIdProp,
  readOnly = false,
  showWallCalendar = false,
  listInitiallyOpen = false,
  employeeName,
  employeeEmail,
  isOwnCabinet = false,
  managerName = null,
}: UserProfileLeavesProps = {}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isPhone = useIsPhone();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams<{
    companyId: string;
    userId: string;
  }>();
  const companyId = companyIdProp || params.companyId;
  const userId = userIdProp || params.userId;

  const [paginationState, setPaginationState] = useState({
    page: 1,
    pageSize: 20,
  });
  const [filters, setFilters] = useState({
    dateFrom: format(startOfYear(new Date()), "yyyy-MM-dd"),
    dateTo: format(endOfYear(new Date()), "yyyy-MM-dd"),
    status: "",
    leaveType: "",
  });
  const [items, setItems] = useState<SerializedLeaveRequest[]>([]);
  const [pagination, setPagination] = useState<{
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Only used when the wall calendar is shown: the list under it starts collapsed unless the caller asks it open. */
  const [isListOpen, setIsListOpen] = useState(listInitiallyOpen);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isPhoneCalendarOpen, setIsPhoneCalendarOpen] = useState(false);
  /** Moves on whenever the person's requests changed, so the list, the calendar and the days used are asked for again. */
  const [reloadKey, setReloadKey] = useState(0);

  const { page, pageSize } = paginationState;
  const pageSizeOptions = [10, 20, 50, 100];

  useEffect(() => {
    if (!companyId || !userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { leaveRequests, pagination: p } =
          await fetchEmployeeLeaveRequests(companyId, userId, {
            from: filters.dateFrom,
            to: filters.dateTo,
            page,
            pageSize,
            ...(filters.status ? { status: filters.status } : {}),
            ...(filters.leaveType ? { leaveType: filters.leaveType } : {}),
          });
        if (cancelled) return;
        setItems(leaveRequests);
        setPagination(p);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : t("profile_leaves_load_error")
          );
          setItems([]);
          setPagination(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, userId, page, pageSize, filters, reloadKey, t]);

  // The «Pildyti» tile on the tasks page links here with ?new=1: open the sheet, and take the
  // mark off the address so that a reload does not open it again.
  useEffect(() => {
    if (!isOwnCabinet || searchParams.get("new") !== "1") return;
    setIsSheetOpen(true);
    const withoutMark = new URLSearchParams(searchParams);
    withoutMark.delete("new");
    setSearchParams(withoutMark, { replace: true });
  }, [isOwnCabinet, searchParams, setSearchParams]);

  // A request just made is shown even when it lies outside the dates the list is filtered by (a January
  // request made in December): the dates are widened to take it in.
  const reloadAfterChange = useCallback(
    (createdLeave?: { startDate: string; endDate: string }) => {
      if (createdLeave) {
        setFilters((current) => ({
          ...current,
          dateFrom: createdLeave.startDate < current.dateFrom ? createdLeave.startDate : current.dateFrom,
          dateTo: createdLeave.endDate > current.dateTo ? createdLeave.endDate : current.dateTo,
        }));
        // A new pending request lands on the first page, whatever page the list was on.
        setPaginationState((prev) => ({ ...prev, page: 1 }));
      }
      setReloadKey((key) => key + 1);
      if (companyId) dispatch(fetchCabinetSummaryThunk(companyId));
    },
    [companyId, dispatch]
  );

  const handlePageChange = (nextPage: number) => {
    if (nextPage > 0 && nextPage !== page) {
      setPaginationState((prev) => ({ ...prev, page: nextPage }));
    }
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    if (nextPageSize > 0 && nextPageSize !== pageSize) {
      setPaginationState({ page: 1, pageSize: nextPageSize });
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPaginationState((prev) => ({ ...prev, page: 1 }));
  };

  const statusOptions = useMemo(
    () => [
      { label: t("all"), value: "" },
      ...LEAVE_STATUSES.map((s) => ({
        label: t(`leave_calendar_status_${s}`),
        value: s,
      })),
    ],
    [t]
  );

  const leaveTypeOptions = useMemo(
    () => [
      { label: t("all"), value: "" },
      ...FIXED_LEAVE_TYPE_CODES.map((code) => ({
        label: t(leaveCalendarLeaveTypeI18nKey(code), { defaultValue: code }),
        value: code,
      })),
    ],
    [t]
  );

  const totalItems =
    typeof pagination?.total === "number" ? pagination.total : items.length;
  const totalPages =
    typeof pagination?.totalPages === "number"
      ? pagination.totalPages
      : totalItems > 0
      ? Math.ceil(totalItems / pageSize)
      : 0;
  const hasNextPage = totalPages > 0 ? page < totalPages : false;
  const hasPreviousPage = page > 1;
  const shouldShowPagination = Boolean(pagination);

  // The card's wall calendar loads its own year and must not wait for the
  // (collapsed) list below it; the full-page spinner is for the profile only.
  if (!showWallCalendar && loading && !pagination && items.length === 0) {
    return <Loading />;
  }

  // The 12-month calendar is about 2500px tall on a phone. Where the list opens by itself (the
  // cabinet) the list is what the person came for, so there it comes first and the calendar
  // waits behind a button.
  const calendarBehindButton = isPhone && showWallCalendar && listInitiallyOpen;
  const wallCalendar = showWallCalendar && companyId && userId && (
    <UserProfileLeavesCalendar
      companyId={companyId}
      userId={userId}
      employeeName={employeeName}
      employeeEmail={employeeEmail}
      refreshKey={reloadKey}
    />
  );

  return (
    <Layout>
      <Layout.Section>
        <div style={{ marginTop: "20px" }}>
          <BlockStack gap="400">
            {isOwnCabinet && companyId && (
              <div className="flex flex-col items-start gap-3">
                <LeaveUsageLine companyId={companyId} refreshKey={reloadKey} />
                <button
                  type="button"
                  className={`${btnPrimaryCls} max-md:w-full`}
                  onClick={() => setIsSheetOpen(true)}
                >
                  {t("cabinet_fill_leave_request")}
                </button>
              </div>
            )}
            {!calendarBehindButton && wallCalendar}
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    {t("profile_leaves_heading")}
                  </Text>
                  {showWallCalendar && (
                    <Button
                      onClick={() => setIsListOpen((open) => !open)}
                      disclosure={isListOpen ? "up" : "down"}
                    >
                      {t("profile_leaves_list_toggle")}
                    </Button>
                  )}
                </InlineStack>

                <Collapsible
                  open={!showWallCalendar || isListOpen}
                  id="user-profile-leaves-list"
                >
                  <BlockStack gap="300">
                    {/* On a phone the dates take a row and the two selects share the next one. */}
                    <InlineStack gap="300" wrap>
                      <div className="min-w-0 flex-[1_1_100%] md:min-w-[300px] md:flex-1">
                        <DateRangePicker
                          start={filters.dateFrom}
                          end={filters.dateTo}
                          onChange={({ start, end }) => {
                            const y0 = format(
                              startOfYear(new Date()),
                              "yyyy-MM-dd"
                            );
                            const y1 = format(
                              endOfYear(new Date()),
                              "yyyy-MM-dd"
                            );
                            setFilters((prev) => ({
                              ...prev,
                              dateFrom: start || y0,
                              dateTo: end || y1,
                            }));
                            setPaginationState((prev) => ({
                              ...prev,
                              page: 1,
                            }));
                          }}
                        />
                      </div>
                      <div className="min-w-0 flex-[1_1_40%] md:min-w-[160px] md:flex-1">
                        <Select
                          label={t("leave_calendar_status_label")}
                          options={statusOptions}
                          value={filters.status}
                          onChange={(value) =>
                            handleFilterChange("status", value)
                          }
                        />
                      </div>
                      <div className="min-w-0 flex-[1_1_40%] md:min-w-[160px] md:flex-1">
                        <Select
                          label={t("leave_calendar_modal_type")}
                          options={leaveTypeOptions}
                          value={filters.leaveType}
                          onChange={(value) =>
                            handleFilterChange("leaveType", value)
                          }
                        />
                      </div>
                    </InlineStack>

                    {error && (
                      <Text as="p" tone="critical">
                        {error}
                      </Text>
                    )}

                    {items.length > 0 && companyId ? (
                      <>
                        <div
                          aria-busy={loading}
                          className={loading ? "opacity-60" : undefined}
                        >
                          {items.map((leave) => (
                            <LeaveRow
                              key={leave.id}
                              leave={leave}
                              companyId={companyId}
                              readOnly={readOnly}
                              isOwnCabinet={isOwnCabinet}
                              onChanged={reloadAfterChange}
                            />
                          ))}
                        </div>
                        {shouldShowPagination && (
                          <div
                            style={{
                              borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                              padding: "16px",
                            }}
                          >
                            <PaginationControls
                              page={page}
                              pageSize={pageSize}
                              total={totalItems}
                              totalPages={totalPages}
                              hasNextPage={hasNextPage}
                              hasPreviousPage={hasPreviousPage}
                              onPageChange={handlePageChange}
                              onPageSizeChange={handlePageSizeChange}
                              pageSizeOptions={pageSizeOptions}
                              isLoading={loading}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      !loading && (
                        <Text as="p" tone="subdued">
                          {t("profile_no_leave_requests")}
                        </Text>
                      )
                    )}
                  </BlockStack>
                </Collapsible>
              </BlockStack>
            </Card>
            {calendarBehindButton && (
              <>
                <Button
                  fullWidth
                  size="large"
                  onClick={() => setIsPhoneCalendarOpen((open) => !open)}
                  disclosure={isPhoneCalendarOpen ? "up" : "down"}
                  ariaExpanded={isPhoneCalendarOpen}
                >
                  {t("leave_calendar")}
                </Button>
                {isPhoneCalendarOpen && wallCalendar}
              </>
            )}
          </BlockStack>
        </div>
        {isOwnCabinet && isSheetOpen && companyId && (
          <LeaveRequestSheet
            companyId={companyId}
            managerName={managerName}
            onClose={() => setIsSheetOpen(false)}
            onRequestCreated={reloadAfterChange}
          />
        )}
      </Layout.Section>
    </Layout>
  );
}

export default UserProfileLeaves;
